import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CreditSummaryService } from 'src/app/services/credit-summary/credit-summary.service';

/**
 * BorrowerKpiComponent displays the key performance indicators for a borrower
 * using the new credit summary endpoint
 */
@Component({
    selector: 'app-borrower-kpi',
    templateUrl: './borrower-kpi.component.html',
    styleUrl: './borrower-kpi.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BorrowerKpiComponent implements OnInit, OnDestroy {
  /** Dependency injections */
  private _creditSummaryService = inject(CreditSummaryService);

  /** Credit summary data */
  creditLimit: number | null = null;
  creditInUse: number | null = null;
  authorizedHolds: number | null = null;
  remainingCredit: number | null = null;

  /** Subscriptions */
  private _subscriptions: Subscription[] = [];

  /** Loading flag */
  isLoading = true;

  /**
   * OnInit lifecycle hook fetches credit summary for the user and subscribes to changes
   */
  async ngOnInit(): Promise<void> {
    // Setup subscriptions to credit summary observables
    this._subscriptions.push(this._creditSummaryService.creditLimit$.subscribe(limit => {
      this.creditLimit = limit
      this._isLoadingCheck();
    }));
    this._subscriptions.push(this._creditSummaryService.creditInUse$.subscribe(inUse => {
      this.creditInUse = inUse
      this._isLoadingCheck();
    }));
    this._subscriptions.push(this._creditSummaryService.authorizedHolds$.subscribe(holds => {
      this.authorizedHolds = holds
      this._isLoadingCheck();
    }));
    this._subscriptions.push(this._creditSummaryService.remainingCredit$.subscribe(remaining => {
      this.remainingCredit = remaining
      this._isLoadingCheck();
    }));

    // Fetch initial credit summary data
    this.creditLimit = this._creditSummaryService.creditLimit;
    this.creditInUse = this._creditSummaryService.creditInUse;
    this.authorizedHolds = this._creditSummaryService.authorizedHolds;
    this.remainingCredit = this._creditSummaryService.remainingCredit;

    this._isLoadingCheck();
  }

  /**
   * Cleanup subscriptions on component destroy
   */
  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Perform a null check on all kpi values to update loading state
   */
  private _isLoadingCheck(): void {
    if (!this.isLoading) return;

    this.isLoading = this.creditLimit === null || this.creditInUse === null || this.authorizedHolds === null || this.remainingCredit === null;
  }
}